import React from 'react';
import { Goal, GoalStatus } from '../../types/Goal';

interface GoalListProps {
  goals: Goal[];
  loading?: boolean;
  onGoalClick: (goal: Goal) => void;
  onAddGoal?: () => void;
  showAddButton?: boolean;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  emptyStateButtonText?: string;
}

const GoalList: React.FC<GoalListProps> = ({
  goals,
  loading = false,
  onGoalClick,
  onAddGoal,
  showAddButton = true,
  emptyStateTitle = "No goals yet",
  emptyStateMessage = "Start by creating your first goal to track your progress!",
  emptyStateButtonText = "Create Your First Goal"
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: GoalStatus) => {
    switch (status) {
      case GoalStatus.NOT_STARTED: return '#6c757d';
      case GoalStatus.IN_PROGRESS: return '#007bff';
      case GoalStatus.COMPLETED: return '#28a745';
      case GoalStatus.ON_HOLD: return '#ffc107';
      case GoalStatus.CANCELLED: return '#dc3545';
      default: return '#6c757d';
    }
  };

  const formatStatus = (status: GoalStatus) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <p>Loading goals...</p>
      </div>
    );
  }

  if (goals.length === 0) {
    return (
      <div className="empty-state">
        <h3>{emptyStateTitle}</h3>
        <p>{emptyStateMessage}</p>
        {showAddButton && onAddGoal && (
          <button className="add-goal-btn primary" onClick={onAddGoal}>
            {emptyStateButtonText}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="goals-grid">
      {goals.map(goal => (
        <div 
          key={goal.id} 
          className="goal-card"
          onClick={() => onGoalClick(goal)}
          title="Click to view details"
          style={{ cursor: 'pointer' }}
        >
          <div className="goal-card-header">
            <h3 className="goal-title">
              {goal.title}
            </h3>
            <span 
              className="goal-status-badge"
              style={{ 
                backgroundColor: getStatusColor(goal.status),
                color: '#ffffff'
              }}
            >
              {formatStatus(goal.status)}
            </span>
          </div>
          
          <div className="goal-progress">
            <div className="goal-progress-bar">
              <div 
                className="goal-progress-fill"
                style={{ width: `${goal.progress}%` }}
              ></div>
            </div>
            <span className="goal-progress-text">{goal.progress}%</span>
          </div>
          
          <div className="goal-dates">
            {goal.targetDate && (
              <small>Target: {formatDate(goal.targetDate)}</small>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default GoalList;
